import React, { useState } from 'react';

import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { ReactComponent as IconLoading } from '@whitelabel/media/icons/loading.svg';
import { formatCurrency } from '@whitelabel/helpers/i18n';
import { QUOTE_VALUES, CURRENCIES } from '@whitelabel/helpers/constants';
import { ReactComponent as IconDocument } from '@whitelabel/media/icons/small/document.svg';
import UpdateAddressModal from '../../../UpdateAddressModal';
import SellingPointsList from '../../../SellingPointsList';
import QuoteWeeklyPrice from '../../../QuoteWeeklyPrice';
import {
  StyledGeoMultiQuotesWrapper,
  StyledText,
  StyledIconEdit,
  StyledZipCode,
  StyledDivider,
  StyledSingleLimit,
  StyledDecorationLine,
  StyledEllipse,
  StyledLimitSelection,
  StyledLimitText,
  StyledTertiary,
  StyledButton,
  StyledLoadingWrapper,
  StyledZipCodeText,
} from './style';
import { Postcode } from './Postcode';
import messages from './messages';

// Hard coded values for initial release
// Ticket XCOM-680: https://domainpartners.atlassian.net/browse/XCOM-680
const LIMITS = [2500, 5000, 10000];

interface IGeoMultiQuotesProps {
  quotesLoading: boolean;

  selectedQuoteIndex: number;

  multiQuotes?: {
    id: string;
    totalPrice: number;
    pdsUrl: string;
    securityToken: string;
  }[];

  createMultiQuotes: (...args: any[]) => any;

  selectQuote: (...args: any[]) => any;

  userEnteredPostcode?: string;
  setUserEnteredPostcode: (...args: any[]) => any;
}

export const GeoMultiQuotes = ({
  quotesLoading,
  multiQuotes,
  createMultiQuotes,
  selectedQuoteIndex,
  selectQuote,
  userEnteredPostcode,
  setUserEnteredPostcode,
}: IGeoMultiQuotesProps): JSX.Element => {
  const intl = useIntl();
  const selectedQuote = multiQuotes && multiQuotes[selectedQuoteIndex];
  const quoteValues = JSON.parse(sessionStorage.getItem(QUOTE_VALUES) || '');
  const storedAddress = quoteValues && quoteValues.address;
  const [userAddress, setUserAddress] = useState(storedAddress);
  const [isUpdateAddressModalOpen, setIsUpdateAddressModalOpen] = useState(false);

  const storeAddress = (address: any) => {
    setUserAddress(address);
    sessionStorage.setItem(
      QUOTE_VALUES,
      JSON.stringify({
        address,
      }),
    );
  };

  const { postcode } = userAddress || {};

  const toggleUpdateAddressModal = () => {
    setIsUpdateAddressModalOpen((prev) => !prev);
  };

  const getNewQuote = async (address: any) => {
    const newQuotes = await createMultiQuotes({
      postcode: address.postcode,
      latitude: address.lat,
      longitude: address.lng,
      limits: LIMITS,
    });
    storeAddress(address);
    return newQuotes;
  };

  const renderLimitsSelection = () =>
    multiQuotes &&
    multiQuotes.map((quote, index) => (
      <StyledSingleLimit key={quote.id} onClick={() => selectQuote(index)} active={index === selectedQuoteIndex}>
        <StyledEllipse />
        <StyledLimitText>{formatCurrency(intl.formatNumber, LIMITS[index], CURRENCIES.USD, true)}</StyledLimitText>
      </StyledSingleLimit>
    ));

  const renderMultiQuotes = () => {
    const { id: selectedQuoteID, totalPrice, securityToken, pdsUrl } = selectedQuote || {};
    return (
      <>
        <StyledZipCodeText>
          {intl.formatMessage(messages.leadText)}{' '}
          <StyledZipCode>
            {postcode} <StyledIconEdit onClick={toggleUpdateAddressModal} />
          </StyledZipCode>
        </StyledZipCodeText>
        {multiQuotes && !quotesLoading ? (
          <>
            <QuoteWeeklyPrice totalPrice={totalPrice || 0} />
            <StyledDivider />
            <StyledText>{intl.formatMessage(messages.adjustLimitText)}</StyledText>
            <StyledLimitSelection>
              <StyledDecorationLine />
              {renderLimitsSelection()}
            </StyledLimitSelection>
          </>
        ) : (
          <StyledLoadingWrapper>
            <IconLoading />
          </StyledLoadingWrapper>
        )}
        <StyledButton
          id="multi-quotes-cta"
          tag={Link}
          to={`/${intl.locale}/quote/${selectedQuoteID}/policyholder?securityToken=${securityToken}`}
          disabled={quotesLoading || !multiQuotes}
        >
          {intl.formatMessage(messages.ctaButtonText)}
        </StyledButton>
        {multiQuotes && (
          <StyledTertiary
            icon={IconDocument}
            buttonType="tertiary"
            href={pdsUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {intl.formatMessage(messages.pdsLinkText)}
          </StyledTertiary>
        )}
        <UpdateAddressModal
          isOpen={isUpdateAddressModalOpen}
          toggle={toggleUpdateAddressModal}
          update={getNewQuote}
          disableManualAddressInput
        />
      </>
    );
  };

  return (
    <StyledGeoMultiQuotesWrapper>
      {userEnteredPostcode ? (
        renderMultiQuotes()
      ) : (
        <Postcode getNewQuote={getNewQuote} setUserEnteredPostcode={setUserEnteredPostcode} />
      )}
      <StyledDivider />
      <SellingPointsList />
    </StyledGeoMultiQuotesWrapper>
  );
};

export default GeoMultiQuotes;
